/**
 * Form controls the app draws itself rather than leaving to the browser, so that they look the
 * same on every system and match the rest of the app. Each keeps the keyboard and screen-reader
 * behaviour of the control it replaces:
 *
 * - `Select`: a button that opens a list of options (the ARIA listbox pattern). Arrow keys, Home
 *   and End move through the options, Enter or Space chooses one, Escape closes the list, and a
 *   letter moves to the next option that starts with it.
 * - `DatePicker`: a button that opens a calendar, in which arrow keys move between days, Page Up
 *   and Page Down between months, and Enter chooses a day. Its value is a calendar date,
 *   `YYYY-MM-DD`.
 * - `DateTimeField`: a date picker and a time field. A time can be typed, such as 14:05, or
 *   chosen from a menu every 30 minutes, and Up and Down move it by 5 minutes. Its value is
 *   `YYYY-MM-DDTHH:mm`.
 * - `FileInput`: a box that shows the chosen file and takes a file dropped on it.
 * - `Checkbox`, `Radio`: the native inputs, drawn in the app's colours.
 *
 * A select or date picker is named by the label of its field, given as `labelId`, and by its value,
 * so that a screen reader says both, as it does for a native select.
 */

import {
  CalendarDays,
  Check,
  ChevronsUpDown,
  LoaderCircle,
  type LucideIcon,
  Upload,
} from "lucide-react";
import {
  type DragEvent,
  type InputHTMLAttributes,
  type KeyboardEvent,
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

import { focusRing } from "./ui.js";

/** Loads the date picker's calendar, which most pages never show, with its date library. */
const loadCalendar = () => import("./Calendar.js");
const Calendar = lazy(() => loadCalendar().then((module) => ({ default: module.Calendar })));

/** Starts loading the calendar before its picker opens, so that it shows at once. */
function preloadCalendar() {
  loadCalendar().catch(() => {
    // Opening the picker loads it again.
  });
}

/** A closed select or date picker looks like a text field, on both themes (`styles.input`). */
const triggerClass = `mt-1 flex w-full items-center justify-between gap-2 rounded-lg border border-zinc-300 bg-white px-3 py-2 text-left text-sm shadow-sm transition-colors hover:border-zinc-400 disabled:cursor-not-allowed disabled:opacity-50 aria-expanded:border-brand-500 aria-expanded:ring-4 aria-expanded:ring-brand-500/15 dark:border-white/[0.1] dark:bg-ink-950/60 dark:text-zinc-100 dark:shadow-none dark:hover:border-white/20 dark:aria-expanded:border-brand-400/70 dark:aria-expanded:ring-brand-400/10 ${focusRing}`;

/**
 * The panel a select's options or a calendar open in: on the dark theme, the lightest surface,
 * since it sits in front of everything else, with a deep shadow below it.
 */
const panelClass =
  "absolute z-30 mt-1.5 rounded-xl bg-white p-1 shadow-xl ring-1 shadow-zinc-900/10 ring-zinc-900/10 dark:bg-ink-850 dark:shadow-2xl dark:shadow-black/70 dark:ring-white/10";

/** The placeholder's colour, while nothing is chosen. */
const placeholderClass = "text-zinc-400 dark:text-zinc-500";

/** A popover's open state, closed by a press outside it or by the focus leaving it. */
function usePopover() {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) {
      return;
    }
    function onPointerDown(event: MouseEvent) {
      if (root.current && !root.current.contains(event.target as Node)) {
        close();
      }
    }
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open, close]);

  return {
    open,
    setOpen,
    root,
    trigger,
    /** Closes the popover and gives the focus back to its button. */
    dismiss() {
      setOpen(false);
      trigger.current?.focus();
    },
    /** Closes the popover when the focus moves out of it, other than to its own button. */
    onBlur(event: { relatedTarget: EventTarget | null }) {
      const next = event.relatedTarget as Node | null;
      if (next && !root.current?.contains(next)) {
        setOpen(false);
      }
    },
  };
}

export interface SelectOption<T extends string> {
  value: T;
  label: string;
  /** A line under the label, such as what the option means. */
  hint?: string;
}

export function Select<T extends string>({
  id: givenId,
  value,
  options,
  onChange,
  labelId,
  placeholder = "Choose…",
  disabled = false,
  "aria-describedby": describedBy,
}: {
  id?: string;
  value: T | "";
  options: readonly SelectOption<T>[];
  onChange: (value: T) => void;
  /** The ids of what names the select, such as its field's label. Its value is added. */
  labelId?: string;
  placeholder?: string;
  disabled?: boolean;
  "aria-describedby"?: string;
}) {
  const ownId = useId();
  const id = givenId ?? ownId;
  const listId = `${id}-options`;
  const popover = usePopover();
  const selectedIndex = options.findIndex((option) => option.value === value);
  const [active, setActive] = useState(0);
  const list = useRef<HTMLDivElement>(null);
  const selected = options[selectedIndex];

  function open() {
    setActive(Math.max(selectedIndex, 0));
    popover.setOpen(true);
  }

  function choose(index: number) {
    const option = options[index];
    if (option) {
      onChange(option.value);
    }
    popover.dismiss();
  }

  // The open list takes the focus, and keeps its active option in view.
  useEffect(() => {
    if (popover.open) {
      list.current?.focus();
    }
  }, [popover.open]);
  useEffect(() => {
    if (popover.open) {
      list.current
        ?.querySelector<HTMLElement>(`[data-index="${active}"]`)
        ?.scrollIntoView?.({ block: "nearest" });
    }
  }, [popover.open, active]);

  function onButtonKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      open();
    }
  }

  function onListKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const last = options.length - 1;
    const moves: Record<string, () => number> = {
      ArrowDown: () => Math.min(active + 1, last),
      ArrowUp: () => Math.max(active - 1, 0),
      Home: () => 0,
      End: () => last,
      PageDown: () => Math.min(active + 8, last),
      PageUp: () => Math.max(active - 8, 0),
    };
    const move = moves[event.key];
    if (move) {
      event.preventDefault();
      setActive(move());
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      choose(active);
    } else if (event.key === "Escape") {
      event.preventDefault();
      popover.dismiss();
    } else if (event.key === "Tab") {
      popover.setOpen(false);
    } else if (event.key.length === 1 && /\S/.test(event.key)) {
      // The next option that starts with the letter typed.
      const letter = event.key.toLowerCase();
      for (let step = 1; step <= options.length; step++) {
        const index = (active + step) % options.length;
        if (options[index]?.label.toLowerCase().startsWith(letter)) {
          setActive(index);
          break;
        }
      }
    }
  }

  return (
    <div ref={popover.root} className="relative">
      <button
        ref={popover.trigger}
        id={id}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={popover.open}
        aria-controls={popover.open ? listId : undefined}
        aria-labelledby={labelId ? `${labelId} ${id}` : undefined}
        aria-describedby={describedBy}
        onClick={() => (popover.open ? popover.setOpen(false) : open())}
        onKeyDown={onButtonKeyDown}
        className={triggerClass}
      >
        <span className={`truncate ${selected ? "" : placeholderClass}`}>
          {selected?.label ?? placeholder}
        </span>
        <ChevronsUpDown aria-hidden className="size-4 shrink-0 text-zinc-400 dark:text-zinc-500" />
      </button>
      {popover.open && (
        <div
          ref={list}
          id={listId}
          role="listbox"
          tabIndex={-1}
          aria-labelledby={labelId}
          aria-activedescendant={`${listId}-${active}`}
          onKeyDown={onListKeyDown}
          onBlur={popover.onBlur}
          className={`${panelClass} max-h-64 w-full overflow-y-auto focus:outline-none`}
        >
          {options.map((option, index) => (
            // The listbox holds the focus and handles the keys. An option is only pointed at.
            // biome-ignore lint/a11y/useKeyWithClickEvents: the listbox handles the keys.
            <div
              key={option.value}
              id={`${listId}-${index}`}
              data-index={index}
              role="option"
              tabIndex={-1}
              aria-selected={option.value === value}
              onMouseMove={() => setActive(index)}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(index)}
              className={`flex cursor-pointer items-start gap-2 rounded-lg px-2.5 py-2 text-sm ${
                index === active
                  ? "bg-brand-50 text-brand-950 dark:bg-white/[0.06] dark:text-white"
                  : "text-zinc-800 dark:text-zinc-200"
              }`}
            >
              <Check
                aria-hidden
                className={`mt-0.5 size-4 shrink-0 text-brand-600 dark:text-brand-400 ${
                  option.value === value ? "" : "invisible"
                }`}
              />
              <span className="min-w-0">
                <span className="block">{option.label}</span>
                {option.hint && (
                  <span className="mt-0.5 block text-xs text-zinc-500 dark:text-zinc-400">
                    {option.hint}
                  </span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** A calendar date, `YYYY-MM-DD`, as midnight of that date where the browser is. */
function parseDay(value: string | undefined): Date | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value ?? "");
  return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : undefined;
}

/** A date's calendar date, `YYYY-MM-DD`, where the browser is. */
function formatDay(date: Date): string {
  const pad = (number: number) => String(number).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** A chosen date as the picker shows it, such as "Sat, 10 Oct 2026". */
function showDay(date: Date): string {
  return date.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function DatePicker({
  id: givenId,
  value,
  onChange,
  labelId,
  min,
  max,
  placeholder = "Choose a date",
  "aria-describedby": describedBy,
}: {
  id?: string;
  /** The chosen date, `YYYY-MM-DD`. Empty for none. */
  value: string;
  onChange: (value: string) => void;
  /** The ids of what names the picker, such as its field's label. Its value is added. */
  labelId?: string;
  /** The first and last dates that may be chosen, `YYYY-MM-DD`. */
  min?: string | undefined;
  max?: string | undefined;
  placeholder?: string;
  "aria-describedby"?: string | undefined;
}) {
  const ownId = useId();
  const id = givenId ?? ownId;
  const popover = usePopover();
  const selected = parseDay(value);
  const first = parseDay(min);
  const last = parseDay(max);

  return (
    <div ref={popover.root} className="relative">
      <button
        ref={popover.trigger}
        id={id}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={popover.open}
        aria-labelledby={labelId ? `${labelId} ${id}` : undefined}
        aria-describedby={describedBy}
        onClick={() => popover.setOpen(!popover.open)}
        onPointerEnter={preloadCalendar}
        onFocus={preloadCalendar}
        onKeyDown={(event) => {
          // The calendar takes the focus once it has loaded. Until then, the button has it.
          if (event.key === "Escape" && popover.open) {
            event.preventDefault();
            popover.setOpen(false);
          }
        }}
        className={triggerClass}
      >
        <span className={`truncate ${selected ? "" : placeholderClass}`}>
          {selected ? showDay(selected) : placeholder}
        </span>
        <CalendarDays aria-hidden className="size-4 shrink-0 text-zinc-400 dark:text-zinc-500" />
      </button>
      {popover.open && (
        <div
          role="dialog"
          aria-label="Choose a date"
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              popover.dismiss();
            }
          }}
          onBlur={popover.onBlur}
          className={`${panelClass} left-0`}
        >
          <Suspense
            fallback={
              <div className="grid h-72 w-[16.75rem] place-items-center">
                <LoaderCircle
                  role="status"
                  aria-label="Loading the calendar"
                  className="size-5 animate-spin text-zinc-400"
                />
              </div>
            }
          >
            <Calendar
              selected={selected}
              first={first}
              last={last}
              onSelect={(day) => {
                onChange(formatDay(day));
                popover.dismiss();
              }}
            />
          </Suspense>
        </div>
      )}
    </div>
  );
}

/** The menu's times, every 30 minutes of a day, `HH:mm`. Any other minute can be typed. */
const TIMES = Array.from(
  { length: 48 },
  (_, index) => `${String(Math.floor(index / 2)).padStart(2, "0")}:${index % 2 ? "30" : "00"}`,
);

/** Minutes the Up and Down keys move a time by. */
const TIME_STEP_MINUTES = 5;

/**
 * A typed time as `HH:mm`, or null when it is not one. It takes "14:05", "9:05", "1405" and
 * "905".
 */
export function parseTime(text: string): string | null {
  const match = /^(\d{1,2}):?(\d{2})$/.exec(text.trim());
  if (!match) {
    return null;
  }
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) {
    return null;
  }
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

/** `time` moved by `minutes`, around midnight if it passes it. */
function shiftTime(time: string, minutes: number): string {
  const [hours = 0, mins = 0] = time.split(":").map(Number);
  const total = (((hours * 60 + mins + minutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

/**
 * A time of day, `HH:mm`, in a text field with a menu of times every 30 minutes (the ARIA
 * combobox pattern). The focus stays in the field. Typing takes any minute, and the time counts
 * once it reads HH:mm or the field is left. Up and Down move it by 5 minutes, Alt and Down opens
 * the menu, and a click on the field opens it too. In the open menu, Up and Down move between
 * times, Enter chooses one and Escape closes it. A typed time that is not one goes back to the
 * last time when the field is left.
 */
function TimeField({
  id,
  value,
  onChange,
  labelId,
}: {
  id: string;
  value: string;
  onChange: (time: string) => void;
  labelId: string;
}) {
  const listId = `${id}-options`;
  const [text, setText] = useState(value);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const root = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const valid = parseTime(text) !== null;

  // A time set from outside shows in the field, unless the field already reads it.
  useEffect(() => {
    setText((current) => (parseTime(current) === value ? current : value));
  }, [value]);

  // A press outside closes the menu.
  useEffect(() => {
    if (!open) {
      return;
    }
    function onPointerDown(event: MouseEvent) {
      if (root.current && !root.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  // The active time stays in view.
  useEffect(() => {
    if (open) {
      list.current
        ?.querySelector<HTMLElement>(`[data-index="${active}"]`)
        ?.scrollIntoView?.({ block: "nearest" });
    }
  }, [open, active]);

  function showMenu() {
    // The menu opens at the current time, or the next one after it.
    const current = parseTime(text) ?? value;
    const next = TIMES.findIndex((time) => time >= current);
    setActive(next === -1 ? TIMES.length - 1 : next);
    setOpen(true);
  }

  function choose(time: string) {
    onChange(time);
    setText(time);
    setOpen(false);
  }

  /** Takes the typed time, or shows the last one again when the text is not a time. */
  function commit() {
    const time = parseTime(text);
    if (time) {
      choose(time);
    } else {
      setText(value);
    }
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (open) {
      const last = TIMES.length - 1;
      const moves: Record<string, () => number> = {
        ArrowDown: () => Math.min(active + 1, last),
        ArrowUp: () => Math.max(active - 1, 0),
        PageDown: () => Math.min(active + 8, last),
        PageUp: () => Math.max(active - 8, 0),
      };
      const move = moves[event.key];
      if (move) {
        event.preventDefault();
        setActive(move());
      } else if (event.key === "Enter") {
        event.preventDefault();
        choose(TIMES[active] ?? value);
      } else if (event.key === "Escape") {
        event.preventDefault();
        setOpen(false);
      } else if (event.key === "Tab") {
        setOpen(false);
      }
      return;
    }
    if (event.key === "ArrowDown" && event.altKey) {
      event.preventDefault();
      showMenu();
    } else if (event.key === "ArrowUp" || event.key === "ArrowDown") {
      event.preventDefault();
      const step = event.key === "ArrowUp" ? TIME_STEP_MINUTES : -TIME_STEP_MINUTES;
      choose(shiftTime(parseTime(text) ?? value, step));
    } else if (event.key === "Enter") {
      // Enter takes the typed time rather than sending the form.
      event.preventDefault();
      commit();
    }
  }

  return (
    <div ref={root} className="relative">
      <input
        id={id}
        type="text"
        role="combobox"
        inputMode="numeric"
        autoComplete="off"
        spellCheck={false}
        maxLength={5}
        placeholder="HH:mm"
        aria-labelledby={labelId}
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-autocomplete="list"
        aria-activedescendant={open ? `${listId}-${active}` : undefined}
        aria-invalid={valid ? undefined : true}
        value={text}
        onChange={(event) => {
          const typed = event.target.value;
          setText(typed);
          setOpen(false);
          // A whole HH:mm counts at once. Shorter forms count when the field is left.
          const time = /^\d{2}:\d{2}$/.test(typed) ? parseTime(typed) : null;
          if (time) {
            onChange(time);
          }
        }}
        onClick={() => (open ? setOpen(false) : showMenu())}
        onKeyDown={onKeyDown}
        onBlur={commit}
        className={`${triggerClass} cursor-text pr-8 font-mono tabular-nums aria-invalid:border-red-500 dark:aria-invalid:border-red-400`}
      />
      <ChevronsUpDown
        aria-hidden
        className="pointer-events-none absolute top-1/2 right-3 mt-0.5 size-4 -translate-y-1/2 text-zinc-400 dark:text-zinc-500"
      />
      {open && (
        <div
          ref={list}
          id={listId}
          role="listbox"
          aria-labelledby={labelId}
          className={`${panelClass} max-h-64 w-full overflow-y-auto`}
        >
          {TIMES.map((time, index) => (
            // The field keeps the focus and handles the keys. A time is only pointed at.
            // biome-ignore lint/a11y/useKeyWithClickEvents: the field handles the keys.
            <div
              key={time}
              id={`${listId}-${index}`}
              data-index={index}
              role="option"
              tabIndex={-1}
              aria-selected={time === value}
              onMouseMove={() => setActive(index)}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(time)}
              className={`flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 font-mono text-sm tabular-nums ${
                index === active
                  ? "bg-brand-50 text-brand-950 dark:bg-white/[0.06] dark:text-white"
                  : "text-zinc-800 dark:text-zinc-200"
              }`}
            >
              <Check
                aria-hidden
                className={`size-4 shrink-0 text-brand-600 dark:text-brand-400 ${
                  time === value ? "" : "invisible"
                }`}
              />
              {time}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * A date and a time of day, as `YYYY-MM-DDTHH:mm`, or empty until a date is chosen. The time
 * starts at 00:00 and may be changed before or after the date.
 */
export function DateTimeField({
  id,
  value,
  onChange,
  labelId,
  timeLabel,
  min,
  max,
  "aria-describedby": describedBy,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  /** The id of the field's label, which names the date. */
  labelId: string;
  /** What names the time, such as "Start time (UTC)". */
  timeLabel: string;
  /** The first and last dates that may be chosen, `YYYY-MM-DD`. */
  min?: string | undefined;
  max?: string | undefined;
  "aria-describedby"?: string | undefined;
}) {
  const [date = "", time = ""] = value ? value.split("T") : [];
  // The time chosen before the date.
  const [draft, setDraft] = useState("00:00");
  const timeId = `${id}-time`;

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_6.5rem] gap-2">
      <DatePicker
        id={id}
        value={date}
        onChange={(day) => onChange(`${day}T${time || draft}`)}
        labelId={labelId}
        min={min}
        max={max}
        aria-describedby={describedBy}
      />
      <span id={`${timeId}-label`} className="sr-only">
        {timeLabel}
      </span>
      <TimeField
        id={timeId}
        value={date ? time : draft}
        onChange={(next) => (date ? onChange(`${date}T${next}`) : setDraft(next))}
        labelId={`${timeId}-label`}
      />
    </div>
  );
}

/** Whether a file is of a type an `accept` list names, such as "video/*" or "image/png,.webp". */
function accepts(accept: string, file: File): boolean {
  return accept.split(",").some((entry) => {
    const type = entry.trim().toLowerCase();
    if (type.startsWith(".")) {
      return file.name.toLowerCase().endsWith(type);
    }
    return type.endsWith("/*") ? file.type.startsWith(type.slice(0, -1)) : file.type === type;
  });
}

/** A size in bytes as people read it, such as "12.4 MB". */
function formatSize(bytes: number): string {
  const units = ["KB", "MB", "GB"];
  if (bytes < 1000) {
    return `${bytes} bytes`;
  }
  let size = bytes / 1000;
  let unit = 0;
  while (size >= 1000 && unit < units.length - 1) {
    size /= 1000;
    unit++;
  }
  return `${size.toFixed(1)} ${units[unit]}`;
}

/**
 * A file chooser: a box that opens the browser's file dialog when pressed and takes a file of an
 * accepted type dropped on it, then shows the file's name and size. The native input stays, out
 * of sight, for the keyboard and screen readers.
 */
export function FileInput({
  id: givenId,
  accept,
  file,
  onChange,
  labelId,
  prompt,
  types,
  icon: Icon = Upload,
}: {
  id?: string;
  /** The types the browser offers, as an `accept` list such as "video/*". */
  accept: string;
  file: File | null;
  onChange: (file: File | null) => void;
  /** The id of the field's label, which names the input. */
  labelId: string;
  /** What the box asks for while no file is chosen, such as "Choose the video file". */
  prompt: string;
  /** The types taken, as people read them, such as "MP4, MOV or WebM". */
  types: string;
  icon?: LucideIcon;
}) {
  const ownId = useId();
  const id = givenId ?? ownId;
  const statusId = `${id}-status`;
  const [over, setOver] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  function onDrop(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault();
    setOver(false);
    const dropped = event.dataTransfer.files[0];
    if (!dropped) {
      return;
    }
    if (!accepts(accept, dropped)) {
      setProblem(`That type of file is not accepted. Choose ${types}.`);
      return;
    }
    setProblem(null);
    onChange(dropped);
  }

  return (
    <div className="mt-1">
      <input
        id={id}
        type="file"
        accept={accept}
        aria-labelledby={labelId}
        aria-describedby={statusId}
        onChange={(event) => {
          setProblem(null);
          onChange(event.target.files?.[0] ?? null);
        }}
        className="peer sr-only"
      />
      <label
        htmlFor={id}
        onDragOver={(event) => {
          event.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
        className={`flex cursor-pointer rounded-xl border border-dashed text-sm transition-colors peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-brand-600 ${
          file ? "items-center gap-3 p-3" : "flex-col items-center gap-2 px-4 py-5 text-center"
        } ${
          over
            ? "border-brand-500 bg-brand-50 dark:border-brand-400/60 dark:bg-brand-400/[0.06]"
            : "border-zinc-300 bg-zinc-50/70 hover:border-brand-500 hover:bg-brand-50/50 dark:border-white/[0.14] dark:bg-white/[0.02] dark:hover:border-brand-400/50 dark:hover:bg-brand-400/[0.04]"
        }`}
      >
        <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-white text-brand-600 shadow-sm ring-1 ring-zinc-200 dark:bg-white/[0.05] dark:text-brand-400 dark:shadow-none dark:ring-white/10">
          <Icon aria-hidden className="size-5" />
        </span>
        {file ? (
          <>
            <span id={statusId} className="min-w-0 flex-1">
              <span className="block truncate font-medium">{file.name}</span>
              <span className="block text-xs text-zinc-500 dark:text-zinc-400">
                {formatSize(file.size)}
              </span>
            </span>
            <span aria-hidden className="shrink-0 font-medium text-brand-700 dark:text-brand-400">
              Change
            </span>
          </>
        ) : (
          <span id={statusId}>
            <span className="font-medium text-brand-700 dark:text-brand-400">{prompt}</span>{" "}
            <span className="text-zinc-600 dark:text-zinc-400">or drop it here</span>
            <span className="mt-0.5 block text-xs text-zinc-500 dark:text-zinc-400">{types}</span>
          </span>
        )}
      </label>
      {problem && (
        <p role="alert" className="mt-2 text-sm text-red-700 dark:text-red-400">
          {problem}
        </p>
      )}
    </div>
  );
}

/** A checkbox in the app's colours, with the native input's behaviour. */
export function Checkbox({ className = "", ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <span className={`relative inline-grid size-4 shrink-0 place-items-center ${className}`}>
      <input
        type="checkbox"
        {...props}
        className={`peer size-4 cursor-pointer appearance-none rounded border border-zinc-400 bg-white shadow-sm transition-colors checked:border-brand-600 checked:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/25 dark:bg-ink-950/60 dark:shadow-none dark:checked:border-brand-400 dark:checked:bg-brand-400 ${focusRing}`}
      />
      <Check
        aria-hidden
        strokeWidth={3.5}
        className="pointer-events-none absolute size-3 text-white opacity-0 peer-checked:opacity-100 dark:text-ink-950"
      />
    </span>
  );
}

/** A radio button in the app's colours, with the native input's behaviour. */
export function Radio({ className = "", ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <span className={`relative inline-grid size-4 shrink-0 place-items-center ${className}`}>
      <input
        type="radio"
        {...props}
        className={`peer size-4 cursor-pointer appearance-none rounded-full border border-zinc-400 bg-white shadow-sm transition-colors checked:border-brand-600 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/25 dark:bg-ink-950/60 dark:shadow-none dark:checked:border-brand-400 ${focusRing}`}
      />
      <span
        aria-hidden
        className="pointer-events-none absolute size-2 rounded-full bg-brand-600 opacity-0 peer-checked:opacity-100 dark:bg-brand-400"
      />
    </span>
  );
}
